import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { CheckCircle2, ArrowRight, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { getGa4Ids } from '../../utils/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inputClass =
  'w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-sm font-medium text-zinc-950 placeholder:text-zinc-400 focus:outline-none focus:border-blue-600 focus:bg-white transition-colors';

export const AuditModal = ({ open, onOpenChange, magnet, industry }) => {
  const [form, setForm] = useState({ name: '', business: '', email: '', phone: '', website: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) setSuccess(false);
  }, [open]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business || !form.email) {
      toast.error('Please fill in your name, business and email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/enquiries/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          business: form.business,
          email: form.email,
          phone: form.phone,
          website: form.website || null,
          industry: industry || null,
          package_interest: magnet || 'Free Audit',
          source: 'free_audit',
        }),
      });
      if (!res.ok) throw new Error('Request failed');
      setSuccess(true);
      setForm({ name: '', business: '', email: '', phone: '', website: '' });
    } catch (err) {
      toast.error('Something went wrong. Please try again or email hello@brightlabs.live');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="agency-page max-w-lg bg-white border border-zinc-200 text-zinc-950 rounded-3xl p-0 overflow-hidden gap-0 shadow-2xl" data-testid="audit-modal">
        <DialogTitle className="sr-only">Request your free audit</DialogTitle>
        {success ? (
          <div className="p-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center mb-5">
              <CheckCircle2 className="text-blue-600" size={26} />
            </div>
            <h3 className="font-heading text-2xl font-bold tracking-tight mb-2">Audit requested.</h3>
            <p className="text-sm text-zinc-500 leading-relaxed mb-7 font-medium">
              We'll review your online presence and send your audit within 2 working days — straight talk on what's working, what's leaking, and what we'd fix first.
            </p>
            <button
              data-testid="audit-success-close"
              onClick={() => onOpenChange(false)}
              className="bg-zinc-950 text-white hover:bg-zinc-800 transition-colors px-7 py-3 rounded-full font-semibold text-sm active:scale-95"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8">
            <div className="flex items-center gap-2 mb-2">
              <Search size={13} className="text-blue-600" />
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-600">Free Audit</p>
            </div>
            <h3 className="font-heading text-2xl font-bold tracking-tight mb-1">{magnet || 'Free Website & Lead Generation Audit'}</h3>
            <p className="text-sm text-zinc-500 mb-6 font-medium">Completely free. No obligation. Delivered within 2 working days.</p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <input data-testid="audit-name-input" className={inputClass} placeholder="Your name" value={form.name} onChange={set('name')} />
              <input data-testid="audit-business-input" className={inputClass} placeholder="Business name" value={form.business} onChange={set('business')} />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input data-testid="audit-email-input" type="email" className={inputClass} placeholder="Email" value={form.email} onChange={set('email')} />
              <input data-testid="audit-phone-input" type="tel" className={inputClass} placeholder="Phone (optional)" value={form.phone} onChange={set('phone')} />
            </div>
            <input
              data-testid="audit-website-input"
              className={`${inputClass} mb-6`}
              placeholder="Your website (or 'none yet')"
              value={form.website}
              onChange={set('website')}
            />

            <button
              type="submit"
              data-testid="audit-submit-button"
              disabled={submitting}
              className="w-full bg-zinc-950 text-white hover:bg-zinc-800 transition-colors px-6 py-3.5 rounded-full font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99]"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Request My Free Audit <ArrowRight size={15} /></>}
            </button>
            <p className="text-[11px] text-zinc-400 text-center mt-4 font-medium">No spam, no pressure. Your details stay private.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
